/**
 * donnees — outillage partagé du dossier données/statistiques (4e, thème #4).
 * Importé par gen_164 et gen_165 seulement.
 *
 * Calculs EXACTS (entiers par construction quand les bornes le garantissent) :
 * proportionnalité du diagramme circulaire (O1) et médiane (O2).
 * Tout re-dérivable depuis les données du tirage → figure et énoncé cohérents.
 *
 * Revue 2026-09-23 (vague 3) : angleSecteur, angleEffectif (sans appelant) et
 * pourcentageSecteur (appelant sorti avec la carte « Du pourcentage à
 * l'effectif » de 164) retirés ; ajout de imgAvecAlt (figure d'énoncé avec son
 * texte de remplacement, que imgElement du module de 5e ne pose pas),
 * svgBatons (diagramme en bâtons de 165), developper, moyenne, positionsMilieu.
 */

// ─── Figures ─────────────────────────────────────────────────

/**
 * Élément additionnel d'ÉNONCÉ : SVG en data-URL dans config.imageUrl (jamais
 * imageData) avec son `alt`, lu à l'écran et à l'impression quand l'image
 * manque. Même forme que imgElement (5eme/statistiques/svgStats), plus l'alt.
 */
export const imgAvecAlt = (svg, alt, maxWidth = 'small') => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth, alt,
    } }],
});

/**
 * Diagramme en bâtons d'une série valeur → effectif (gen_165). Écrit ici et non
 * pris à svgBarres (5eme/statistiques/svgStats) : dans svgBarres le nom de
 * l'axe vertical est posé à y = 10, sur la graduation du haut, et les deux se
 * chevauchent (vu au rendu, 2026-09-23) ; ce module de 5e sert d'autres gens,
 * il n'est pas modifié. Ici une marge haute réservée au nom d'axe, des bâtons
 * fins, l'axe des effectifs gradué de 1 en 1 de 0 au plus grand effectif,
 * aucun effectif écrit au-dessus des bâtons ; un effectif nul laisse la valeur
 * étiquetée sans bâton. Mêmes conventions de balisage que svgBarres
 * (graduations text-anchor="end", bâtons fill="#1E3A8A", abscisses
 * text-anchor="middle").
 */
export const svgBatons = (valeurs, effectifs, { axeX = '', axeY = 'Effectifs' } = {}) => {
    const n = valeurs.length;
    const colW = 44, mL = 44, mT = 30, plotH = 170, mB = 46;
    const W = mL + n * colW + 16;
    const H = mT + plotH + mB;
    const yMax = Math.max(1, ...effectifs);
    const yPix = (v) => mT + plotH - (v / yMax) * plotH;
    let grille = '';
    for (let v = 0; v <= yMax; v++) {
        const y = yPix(v).toFixed(1);
        grille += `<line x1="${mL}" y1="${y}" x2="${W - 8}" y2="${y}" stroke="#cbd5e1" stroke-width="1" stroke-dasharray="3,3"/>`
            + `<text x="${mL - 6}" y="${(+y + 4).toFixed(1)}" font-size="12" fill="#334155" text-anchor="end">${v}</text>`;
    }
    let batons = '';
    valeurs.forEach((val, i) => {
        const cx = mL + i * colW + colW / 2;
        const e = effectifs[i];
        if (e > 0) {
            const haut = yPix(e);
            batons += `<rect x="${(cx - 5).toFixed(1)}" y="${haut.toFixed(1)}" width="10" height="${(mT + plotH - haut).toFixed(1)}" fill="#1E3A8A" fill-opacity="0.8" stroke="#1E3A8A" stroke-width="1"/>`;
        }
        batons += `<text x="${cx}" y="${mT + plotH + 17}" font-size="12" fill="#1e293b" text-anchor="middle">${val}</text>`;
    });
    const axes = `<line x1="${mL}" y1="${mT - 8}" x2="${mL}" y2="${mT + plotH}" stroke="#334155" stroke-width="1.5"/>`
        + `<line x1="${mL}" y1="${mT + plotH}" x2="${W - 8}" y2="${mT + plotH}" stroke="#334155" stroke-width="1.5"/>`;
    const noms = `<text x="6" y="14" font-size="12" fill="#334155">${axeY}</text>`
        + (axeX ? `<text x="${mL + (W - mL) / 2}" y="${H - 8}" font-size="12" fill="#334155" text-anchor="middle">${axeX}</text>` : '');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">`
        + `<rect x="0" y="0" width="${W}" height="${H}" fill="#f8fafc"/>${grille}${axes}${batons}${noms}</svg>`;
};

// ─── Diagramme circulaire (O1) ───────────────────────────────

/** Effectif d'un secteur : `pct` % d'un total. Entier si total multiple de 20 (pct multiple de 5). */
export const effectifSecteur = (pct, total) => (pct * total) / 100;

// ─── Médiane (O2) ────────────────────────────────────────────

/** Copie triée croissante (numérique). */
export const trierNb = (vals) => [...vals].sort((a, b) => a - b);

/** Écriture française d'un nombre : point décimal → virgule (« 38.5 » → « 38,5 »). */
export const frNb = (x) => String(x).replace('.', ',');

/**
 * Médiane d'une série (effectif = vals.length).
 * - effectif impair : valeur du milieu (rang (n+1)/2) ;
 * - effectif pair   : (a + b) : 2 des deux valeurs du milieu (peut valoir x,5).
 * Renvoie un NOMBRE exact (la moitié de la somme de deux entiers est un multiple de 0,5).
 */
export const mediane = (vals) => {
    const s = trierNb(vals);
    const n = s.length;
    return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
};

/** Les deux valeurs du milieu d'un effectif PAIR, triées (rangs n/2 et n/2+1). */
export const valeursCentralesPair = (vals) => {
    const s = trierNb(vals);
    const n = s.length;
    return [s[n / 2 - 1], s[n / 2]];
};

/** Moyenne arithmétique (nombre, éventuellement non décimal). */
export const moyenne = (vals) => vals.reduce((a, b) => a + b, 0) / vals.length;

/** Série développée d'un tableau valeur → effectif (valeurs dans l'ordre du tableau). */
export const developper = (valeurs, effectifs) =>
    valeurs.flatMap((v, i) => Array(effectifs[i]).fill(v));

/**
 * Rangs (base 1) des valeurs « du milieu » d'une liste de longueur n :
 * [(n+1)/2] si n impair, [n/2, n/2 + 1] si n pair.
 */
export const positionsMilieu = (n) => (n % 2 ? [(n + 1) / 2] : [n / 2, n / 2 + 1]);
